// src/edu-world/ui/mobile-dpad.ts
// Virtual D-pad for mobile devices.
// Renders directional buttons on screen and feeds input to PlayerController.
// Only visible on touch devices.

import Phaser from 'phaser';

export interface DpadDirection {
  x: number; // -1, 0, 1
  y: number; // -1, 0, 1
}

/**
 * Mobile D-pad overlay — 4 directional buttons (up/down/left/right)
 * Positioned bottom-left of the screen.
 * Returns current pressed direction each frame.
 */
export class MobileDpad {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private direction: DpadDirection = { x: 0, y: 0 };
  private buttons: Map<string, Phaser.GameObjects.Arc> = new Map();
  private arrows: Map<string, Phaser.GameObjects.Text> = new Map();
  private visible: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0).setDepth(9999).setScrollFactor(0);

    // Always show D-pad (works on both touch and desktop for testing)
    this.visible = true;
    this.createButtons();
    this.positionDpad();
    console.log('[MobileDpad] Created at', this.container.x, this.container.y, 'visible:', this.container.visible, 'children:', this.container.length);

    // Reposition on resize
    scene.scale.on('resize', () => this.positionDpad());
  }

  private createButtons(): void {
    const btnSize = 32;
    const spacing = 56;
    const btnColor = 0xffffff;
    const btnAlpha = 0.45;

    // Button positions relative to center (0,0)
    const positions: { id: string; x: number; y: number; arrow: string }[] = [
      { id: 'up', x: 0, y: -spacing, arrow: '▲' },
      { id: 'down', x: 0, y: spacing, arrow: '▼' },
      { id: 'left', x: -spacing, y: 0, arrow: '◀' },
      { id: 'right', x: spacing, y: 0, arrow: '▶' },
    ];

    for (const pos of positions) {
      // Background circle
      const btn = this.scene.add.circle(pos.x, pos.y, btnSize, btnColor, btnAlpha);
      btn.setInteractive({ useHandCursor: false });
      btn.setScrollFactor(0);
      this.buttons.set(pos.id, btn);
      this.container.add(btn);

      // Arrow text
      const arrow = this.scene.add.text(pos.x, pos.y, pos.arrow, {
        fontSize: '20px',
        color: '#ffffff',
      }).setOrigin(0.5).setScrollFactor(0).setAlpha(0.6);
      this.arrows.set(pos.id, arrow);
      this.container.add(arrow);

      // Touch events
      btn.on('pointerdown', () => this.pressDirection(pos.id));
      btn.on('pointerup', () => this.releaseDirection(pos.id));
      btn.on('pointerout', () => this.releaseDirection(pos.id));
    }

    // Center dot (visual reference)
    const center = this.scene.add.circle(0, 0, 12, 0xffffff, 0.15);
    center.setScrollFactor(0);
    this.container.add(center);
  }

  private pressDirection(id: string): void {
    const btn = this.buttons.get(id);
    if (btn) btn.setAlpha(0.6);
    const arrow = this.arrows.get(id);
    if (arrow) arrow.setAlpha(1);

    switch (id) {
      case 'up': this.direction.y = -1; break;
      case 'down': this.direction.y = 1; break;
      case 'left': this.direction.x = -1; break;
      case 'right': this.direction.x = 1; break;
    }
  }

  private releaseDirection(id: string): void {
    const btn = this.buttons.get(id);
    if (btn) btn.setAlpha(0.3);
    const arrow = this.arrows.get(id);
    if (arrow) arrow.setAlpha(0.6);

    switch (id) {
      case 'up': if (this.direction.y === -1) this.direction.y = 0; break;
      case 'down': if (this.direction.y === 1) this.direction.y = 0; break;
      case 'left': if (this.direction.x === -1) this.direction.x = 0; break;
      case 'right': if (this.direction.x === 1) this.direction.x = 0; break;
    }
  }

  private positionDpad(): void {
    if (!this.visible) return;
    const cam = this.scene.cameras.main;
    const w = cam.width;
    const h = cam.height;
    // Bottom-left, with padding that works in both orientations
    const padX = Math.min(90, w * 0.12);
    const padY = h - Math.min(90, h * 0.22);
    this.container.setPosition(padX, padY);
  }

  /** Get current direction — called each frame by PlayerController */
  getDirection(): DpadDirection {
    return this.direction;
  }

  /** Check if D-pad is being pressed */
  isPressed(): boolean {
    return this.direction.x !== 0 || this.direction.y !== 0;
  }

  /** Show/hide the D-pad */
  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }
}
